import { useEffect, useMemo } from 'react'
import { useSWR } from 'modules/network/hooks/useSwr'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useLoansList } from 'modules/pools/hooks/useLoansList'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'

import {
  PoolAsset,
  getERCAssetByAddress,
} from 'modules/pools/constants/poolAssets'
import { weiToNum } from 'modules/blockChain/utils/parseWei'
import { getAssetPairPrice } from 'modules/blockChain/utils/getAssetPairPrice'
import { POOL_INVESTORS_COLLECTION } from 'modules/pools/utils/getPoolInvestorContract'

export function useTotalDebt() {
  const { chainId } = useWeb3()
  const { data: loans } = useLoansList()
  const { getAssetDecimals } = useAssetsData()

  const totalOweSwr = useSWR(
    loans && loans.length ? `you-owe-${chainId}` : null,
    async () => {
      const _loans = loans
      if (!_loans || !_loans.length) return null

      const filtered = _loans.filter(({ loan }) => loan.status === 6)

      const assets = filtered.reduce<PoolAsset[]>((res, curr) => {
        const asset = getERCAssetByAddress(curr.loan.ERC20Address, chainId)
        if (asset && !res.includes(asset)) res.push(asset)
        return res
      }, [])

      const prices = await Promise.all(
        assets.map(async asset =>
          getAssetPairPrice({
            assetA: asset,
            assetB: 'USD',
            chainId,
          }),
        ),
      )

      const assetPriceMap = assets.reduce<Record<string, typeof prices[0]>>(
        (res, asset, i) => ({
          ...res,
          [asset]: prices[i],
        }),
        {},
      )

      const owe = filtered.reduce((res, curr) => {
        const asset = getERCAssetByAddress(curr.loan.ERC20Address, chainId)
        const assetData = assetPriceMap[asset!]
        const decimals = getAssetDecimals(asset)
        const totalDebt = weiToNum(
          curr.loan.totalPaymentsValue.sub(curr.loan.paymentComplete),
          decimals,
        )
        const priceNum = weiToNum(assetData.price, assetData.decimals)
        const totalDebtUSD = totalDebt * priceNum
        return res + totalDebtUSD
      }, 0)

      return owe
    },
  )

  const revalidate = totalOweSwr.mutate

  useEffect(() => {
    revalidate()
  }, [loans, revalidate])

  const poolNumber = useMemo(() => {
    if (!loans || !loans.length) return
    const { poolAddress } = loans[0].loan
    const idx = POOL_INVESTORS_COLLECTION.findIndex(
      i => i.contract.chainAddress.get(chainId) === poolAddress,
    )
    return idx + 1
  }, [chainId, loans])

  return {
    totalOwe: totalOweSwr.data,
    totalOweSwr,
    poolNumber,
  }
}
